import { useState, useMemo } from 'react'
import { motion } from 'framer-motion'
import { ChevronLeft, ChevronRight, Loader2, Inbox } from 'lucide-react'
import { formatPercent } from '@/utils/format-currency'
import { formatDealValue } from '@/utils/dealDisplay'
import { Badge, getStatusVariant } from '@/components/ui/Badge'
import { cn } from '@/utils/cn'
import { getStageLabel } from './deals-constants'
import type { Deal } from '@/types'
import { sortDealsForTable, type DealsTableSortKey, type SortDirection } from './dealsTableSorting'
import { DealsTableSortableHeader } from './DealsTableSortableHeader'

const PAGE_SIZE = 10

interface DealsTableViewProps {
  visibleDeals: Deal[]
  onSelectDeal: (deal: Deal) => void
  areValuesVisible?: boolean
  loading?: boolean
}

export function DealsTableView({
  visibleDeals,
  onSelectDeal,
  areValuesVisible = false,
  loading = false,
}: DealsTableViewProps) {
  const [page, setPage] = useState(1)
  const [sortKey, setSortKey] = useState<DealsTableSortKey>('empresa')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')

  const sortedDeals = useMemo(
    () => sortDealsForTable(visibleDeals, sortKey, sortDirection),
    [visibleDeals, sortKey, sortDirection],
  )

  const totalPages = Math.max(1, Math.ceil(sortedDeals.length / PAGE_SIZE))
  const paginatedDeals = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE
    return sortedDeals.slice(start, start + PAGE_SIZE)
  }, [sortedDeals, page])

  const handleSort = (key: DealsTableSortKey) => {
    if (sortKey === key) {
      setSortDirection((direction) => direction === 'asc' ? 'desc' : 'asc')
      return
    }
    setSortKey(key)
    setSortDirection('asc')
  }

  // Reset to page 1 when filters change
  const totalRef = useMemo(() => visibleDeals.length, [visibleDeals])
  const [prevTotal, setPrevTotal] = useState(totalRef)
  if (totalRef !== prevTotal) {
    setPrevTotal(totalRef)
    if (page > 1) setPage(1)
  }

  // Loading state
  if (loading) {
    return (
      <div className="glass-card flex items-center justify-center py-16">
        <div className="flex flex-col items-center gap-3">
          <Loader2 size={28} className="text-accent animate-spin" />
          <p className="text-text-label text-sm">Carregando deals...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="glass-card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full" aria-label="Lista de deals do pipeline">
          <caption className="sr-only">
            Tabela de deals com empresa, valor, probabilidade, closer e status. Clique em uma linha para abrir detalhes.
          </caption>
          <thead>
            <tr className="border-b border-white/[0.06]">
              <DealsTableSortableHeader label="Empresa" sortKey="empresa" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
              <DealsTableSortableHeader label="Deal" sortKey="deal" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
              <DealsTableSortableHeader label="Valor" sortKey="valor" activeKey={sortKey} direction={sortDirection} onSort={handleSort} align="right" />
              <DealsTableSortableHeader label="Probabilidade" sortKey="probabilidade" activeKey={sortKey} direction={sortDirection} onSort={handleSort} align="center" />
              <DealsTableSortableHeader label="Closer" sortKey="closer" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
              <DealsTableSortableHeader label="Próx. Ação" sortKey="proximaAcao" activeKey={sortKey} direction={sortDirection} onSort={handleSort} />
              <DealsTableSortableHeader label="Status" sortKey="status" activeKey={sortKey} direction={sortDirection} onSort={handleSort} align="center" />
            </tr>
          </thead>
          <tbody>
            {paginatedDeals.map((deal, i) => (
              <motion.tr
                key={deal.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.02, duration: 0.2 }}
                onClick={() => onSelectDeal(deal)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    onSelectDeal(deal)
                  }
                }}
                tabIndex={0}
                role="button"
                aria-label={`Abrir deal ${deal.empresaNome}, etapa ${getStageLabel(deal.etapa)}`}
                className={cn(
                  'border-b border-white/[0.03] cursor-pointer',
                  'hover:bg-white/[0.03] transition-colors duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
                  deal.stagnant && 'bg-amber-500/[0.03]'
                )}
              >
                {/* Empresa */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-semibold text-accent">
                        {deal.empresaNome?.[0] || '?'}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-text-primary truncate max-w-[180px]">
                      {deal.empresaNome}
                    </span>
                  </div>
                </td>

                {/* Deal nome */}
                <td className="px-4 py-3">
                  <span className="text-sm text-text-secondary truncate max-w-[160px] block">
                    {deal.nome}
                  </span>
                </td>

                {/* Valor */}
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-semibold text-text-primary">
                    {formatDealValue(deal.valor, areValuesVisible)}
                  </span>
                  {areValuesVisible && deal.recorrencia && (
                    <span className="block text-xs text-text-label">
                      {deal.recorrencia}
                    </span>
                  )}
                </td>

                {/* Probabilidade */}
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-12 h-1.5 bg-white/[0.06] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-accent transition-all"
                        style={{ width: `${deal.probabilidade}%` }}
                      />
                    </div>
                    <span className="text-xs text-text-label w-8 text-right">
                      {formatPercent(deal.probabilidade, 0)}
                    </span>
                  </div>
                </td>

                {/* Closer */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <div className="w-5 h-5 rounded-full bg-accent/20 flex items-center justify-center">
                      <span className="text-[9px] font-semibold text-accent">
                        {deal.closerAvatar || deal.closerNome?.[0] || '?'}
                      </span>
                    </div>
                    <span className="text-sm text-text-secondary truncate max-w-[100px]">
                      {deal.closerNome?.split(' ')[0] || '—'}
                    </span>
                  </div>
                </td>

                {/* Próxima ação */}
                <td className="px-4 py-3">
                  <span className="text-sm text-text-label">
                    {deal.dataReuniao
                      ? new Date(deal.dataReuniao).toLocaleDateString('pt-BR')
                      : deal.dataFechamentoPrevisto
                        ? new Date(deal.dataFechamentoPrevisto).toLocaleDateString('pt-BR')
                        : '—'}
                  </span>
                </td>

                {/* Status */}
                <td className="px-4 py-3 text-center">
                  <Badge variant={getStatusVariant(deal.etapa)}>
                    {getStageLabel(deal.etapa)}
                  </Badge>
                </td>
              </motion.tr>
            ))}

            {visibleDeals.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-white/[0.04] flex items-center justify-center">
                      <Inbox size={18} className="text-text-muted" />
                    </div>
                    <p className="text-text-label text-sm">Nenhum deal encontrado</p>
                    <p className="text-text-muted text-xs">Ajuste os filtros para ver mais resultados.</p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {visibleDeals.length > PAGE_SIZE && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-white/[0.06]">
          <span className="text-xs text-text-label">
            {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, visibleDeals.length)} de {visibleDeals.length}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className={cn(
                'w-8 h-8 flex items-center justify-center rounded-md transition-all',
                page <= 1
                  ? 'text-text-muted/30 cursor-not-allowed'
                  : 'text-text-muted hover:text-text-primary hover:bg-white/[0.06]'
              )}
              title="Página anterior"
            >
              <ChevronLeft size={14} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <button
                key={p}
                onClick={() => setPage(p)}
                className={cn(
                  'w-8 h-8 flex items-center justify-center rounded-md text-xs font-medium transition-all',
                  p === page
                    ? 'bg-accent text-white'
                    : 'text-text-muted hover:text-text-primary hover:bg-white/[0.06]'
                )}
              >
                {p}
              </button>
            ))}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className={cn(
                'w-8 h-8 flex items-center justify-center rounded-md transition-all',
                page >= totalPages
                  ? 'text-text-muted/30 cursor-not-allowed'
                  : 'text-text-muted hover:text-text-primary hover:bg-white/[0.06]'
              )}
              title="Próxima página"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
